/**
 * ActivitiesPage — standalone activity log across the workspace.
 * Useful for "what did the team do this week?" view. No `regarding`
 * filter pre-applied; activity rows show their parent.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsActivities,
  useFiasLeadsContacts,
  useFiasLeadsCompanies,
  useDeleteFiasLeadsActivity,
} from '../hooks/useFiasLeads';
import type { FiasLeadsActivityKind } from '@/lib/api';
import { SkeletonRow } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const KIND_COLORS: Record<FiasLeadsActivityKind, string> = {
  note: 'bg-slate-100 text-slate-700',
  call: 'bg-blue-100 text-blue-700',
  email: 'bg-emerald-100 text-emerald-700',
  meeting: 'bg-purple-100 text-purple-700',
};

export function ActivitiesPage({ holderId }: Props) {
  const [kindFilter, setKindFilter] = useState<FiasLeadsActivityKind | ''>('');
  const activitiesQuery = useFiasLeadsActivities(holderId, {
    kind: kindFilter || undefined,
    limit: 100,
  });
  const contactsQuery = useFiasLeadsContacts(holderId);
  const companiesQuery = useFiasLeadsCompanies(holderId);
  const deleteActivity = useDeleteFiasLeadsActivity(holderId);

  const handleDelete = (activityId: string, subject: string) => {
    if (!confirm(`Delete activity "${subject}"?`)) return;
    deleteActivity.mutate(activityId);
  };

  const contactNameById = new Map(
    (contactsQuery.data ?? []).map((c) => [
      c.contactId,
      `${c.firstName} ${c.lastName ?? ''}`.trim(),
    ]),
  );
  const companyNameById = new Map((companiesQuery.data ?? []).map((c) => [c.companyId, c.name]));

  const activities = activitiesQuery.data ?? [];

  function regardingLink(a: (typeof activities)[number]) {
    if (a.regardingType === 'vault_contact') {
      const name = contactNameById.get(a.regardingId) ?? a.regardingId;
      return (
        <Link
          to={`${ARCHE_PATH}/contacts/${a.regardingId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          {name}
        </Link>
      );
    }
    if (a.regardingType === 'vault_company') {
      const name = companyNameById.get(a.regardingId) ?? a.regardingId;
      return (
        <Link
          to={`${ARCHE_PATH}/companies/${a.regardingId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          {name}
        </Link>
      );
    }
    if (a.regardingType === 'fias_leads_deal') {
      return (
        <Link
          to={`${ARCHE_PATH}/deals/${a.regardingId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          deal {a.regardingId.slice(0, 8)}…
        </Link>
      );
    }
    return null;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Activity log</h1>
          <p className="text-sm text-muted-foreground">
            All logged interactions across the workspace.
          </p>
        </div>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as FiasLeadsActivityKind | '')}
          className="rounded border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="">All kinds</option>
          <option value="note">Notes</option>
          <option value="call">Calls</option>
          <option value="email">Emails</option>
          <option value="meeting">Meetings</option>
        </select>
      </header>

      {activitiesQuery.isLoading && <SkeletonRow />}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {activities.map((a) => (
            <li key={a.activityId} className="p-3 flex items-start gap-3">
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-xs uppercase ${KIND_COLORS[a.kind]}`}
              >
                {a.kind}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{a.subject}</p>
                {a.bodyMarkdown && (
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-1">
                    {a.bodyMarkdown}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {regardingLink(a)} · {new Date(a.occurredAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => handleDelete(a.activityId, a.subject)}
                disabled={deleteActivity.isPending}
                aria-label="Delete activity"
                title="Delete activity"
                className="shrink-0 rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        {activities.length === 0 && !activitiesQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">No activities yet.</p>
        )}
      </div>
    </div>
  );
}
